// @ts-nocheck
import React, { useEffect, useState, useCallback, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { api } from '../services/api'

function timeAgo(dateStr: string, t: any, lang: string): string {
  const diff = Math.floor((Date.now() - new Date(dateStr).getTime()) / 1000)
  if (diff < 60) return t('notificationHistory.justNow')
  if (diff < 3600) return t('notificationHistory.minAgo', { min: Math.floor(diff / 60) })
  if (diff < 86400) return t('notificationHistory.hAgo', { h: Math.floor(diff / 3600) })
  return new Date(dateStr).toLocaleString(lang === 'en' ? 'en-GB' : 'sk-SK', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })
}

function EnergyBar({ value, color }: { value: number; color: string }) {
  return (
    <div style={{ flex: 1 }}>
      <div style={{ height: 8, borderRadius: 4, backgroundColor: '#f0f0f0', overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${Math.min(value, 100)}%`, backgroundColor: color, borderRadius: 4, transition: 'width 0.4s' }} />
      </div>
      <span style={{ fontSize: 11, color: '#888', marginTop: 2, display: 'block', textAlign: 'right' }}>{value}</span>
    </div>
  )
}

const EVENT_ICONS: Record<string, string> = {
  warning_1: '⚠️', warning_2: '🚨', warning_3: '🆘',
  ok: '✅', motion_alarm: '🏃', heartbeat: '💚',
}

export default function DeviceDetailRadar({ device: initialDevice }: { device: any }) {
  const [device, setDevice] = useState(initialDevice)
  const [events, setEvents] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [renaming, setRenaming] = useState(false)
  const [newName, setNewName] = useState('')
  const [deleting, setDeleting] = useState(false)
  const [regenConfirm, setRegenConfirm] = useState(false)
  const navigate = useNavigate()
  const { t, i18n } = useTranslation()
  const lang = i18n.language
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const loadDevice = useCallback(async () => {
    if (!device?.id) return
    try {
      const res = await api.get(`/devices/${device.id}`)
      setDevice(res.data.device)
    } catch {}
  }, [device?.id])

  const loadEvents = useCallback(async () => {
    if (!device?.id) return
    try {
      const res = await api.get(`/devices/${device.id}/events`)
      setEvents(res.data.events)
    } catch {}
    finally { setLoading(false) }
  }, [device?.id])

  useEffect(() => {
    if (!device) { navigate('/dashboard'); return }
    loadDevice()
    loadEvents()
    pollRef.current = setInterval(loadDevice, 5000)
    return () => { if (pollRef.current) clearInterval(pollRef.current) }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const handleDelete = async () => {
    if (!window.confirm(t('deviceDetail.deleteConfirm', { name: device.name }))) return
    setDeleting(true)
    try {
      await api.delete(`/devices/${device.id}`)
      navigate('/dashboard')
    } catch {
      alert(t('deviceDetail.deleteError'))
      setDeleting(false)
    }
  }

  const handleRename = async () => {
    if (!newName.trim()) return
    try {
      const res = await api.patch(`/devices/${device.id}/name`, { name: newName.trim() })
      setDevice(res.data.device)
      setRenaming(false)
      setNewName('')
    } catch {}
  }

  const handleRegenKey = async () => {
    if (!window.confirm(t('deviceDetail.regenWarning'))) return
    try {
      const res = await api.post(`/devices/${device.id}/regenerate-key`)
      setDevice(res.data.device)
      setRegenConfirm(true)
      setTimeout(() => setRegenConfirm(false), 5000)
    } catch {}
  }

  const radar = device?.config?.radar
  const isOnline = device?.isOnline
  const rssi = device?.config?.rssi

  const presenceColor = radar?.presence ? '#2D7A3A' : '#888'
  const presenceBg = radar?.presence ? '#e8f5f0' : '#f5f5f5'

  return (
    <div style={S.container}>
      <div style={S.header}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <button style={S.back} onClick={() => navigate('/dashboard')}>← {t('deviceDetail.back')}</button>
          <button style={{ background: 'none', border: 'none', color: '#e74c3c', fontSize: 14, cursor: 'pointer' }}
            onClick={handleDelete} disabled={deleting}>
            {deleting ? t('deviceDetail.deleting') : t('deviceDetail.delete')}
          </button>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
          <span style={{ fontSize: 28 }}>📡</span>
          <h1 style={S.title}>{device?.name}</h1>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <span style={{ ...S.badge, backgroundColor: isOnline ? '#e8f5f0' : '#f5f5f5', color: isOnline ? '#2D7A3A' : '#888' }}>
            {isOnline ? '● Online' : '○ Offline'}
          </span>
          <span style={{ ...S.badge, backgroundColor: '#f0f4ff', color: '#4a6cf7' }}>Radar V2</span>
          {device?.lastSeen && (
            <span style={{ ...S.badge, backgroundColor: '#f8f8f8', color: '#aaa' }}>
              {timeAgo(device.lastSeen, t, lang)}
            </span>
          )}
        </div>
      </div>

      <div style={S.content}>

        {/* Radar live panel */}
        <div style={S.card}>
          <p style={S.cardLabel}>Radar — živý stav</p>

          {/* Presence big indicator */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16, padding: '14px 16px', borderRadius: 12, backgroundColor: presenceBg }}>
            <span style={{ fontSize: 32 }}>{radar?.presence ? '🧍' : '🚶'}</span>
            <div>
              <div style={{ fontSize: 18, fontWeight: 700, color: presenceColor }}>
                {radar?.presence ? 'Prítomnosť detekovaná' : 'Nikto nie je doma'}
              </div>
              {radar?.presence && (
                <div style={{ fontSize: 13, color: '#555', marginTop: 2 }}>
                  {radar?.motion && radar?.stationary && '🏃 pohyb + 🧘 statický'}
                  {radar?.motion && !radar?.stationary && '🏃 pohyb'}
                  {!radar?.motion && radar?.stationary && '🧘 statický'}
                </div>
              )}
            </div>
          </div>

          {radar?.presence ? (
            <>
              {/* Distances */}
              <div style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
                {radar?.motion && (
                  <div style={{ flex: 1, padding: '12px 14px', borderRadius: 10, backgroundColor: '#fff8f0', border: '1px solid #ffe4c4' }}>
                    <div style={{ fontSize: 11, color: '#888', marginBottom: 4 }}>Vzdialenosť pohybu</div>
                    <div style={{ fontSize: 22, fontWeight: 700, color: '#e67e22' }}>{radar.motionDistCm} cm</div>
                  </div>
                )}
                {radar?.stationary && (
                  <div style={{ flex: 1, padding: '12px 14px', borderRadius: 10, backgroundColor: '#f0f4ff', border: '1px solid #d0daff' }}>
                    <div style={{ fontSize: 11, color: '#888', marginBottom: 4 }}>Vzdialenosť statický</div>
                    <div style={{ fontSize: 22, fontWeight: 700, color: '#4a6cf7' }}>{radar.staticDistCm} cm</div>
                  </div>
                )}
              </div>

              {/* Energy bars */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {radar?.motion && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span style={{ fontSize: 12, color: '#555', width: 110, flexShrink: 0 }}>Energia pohybu</span>
                    <EnergyBar value={radar.motionEnergy ?? 0} color="#e67e22" />
                  </div>
                )}
                {radar?.stationary && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span style={{ fontSize: 12, color: '#555', width: 110, flexShrink: 0 }}>Energia statický</span>
                    <EnergyBar value={radar.staticEnergy ?? 0} color="#4a6cf7" />
                  </div>
                )}
              </div>
            </>
          ) : (
            <div style={{ color: '#aaa', fontSize: 14, textAlign: 'center', padding: '8px 0' }}>Radar nezdetegoval žiadneho človeka</div>
          )}

          {!radar && (
            <div style={{ color: '#aaa', fontSize: 14, textAlign: 'center', padding: '8px 0' }}>
              {isOnline ? 'Čakám na prvé dáta z radaru...' : 'Zariadenie je offline'}
            </div>
          )}
        </div>

        {/* WiFi + firmware status */}
        {(rssi !== undefined || device?.config?.firmware) && (
          <div style={S.card}>
            <p style={S.cardLabel}>{t('deviceDetail.deviceStatus')}</p>
            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
              {rssi !== undefined && (
                <div style={S.statusChip}>
                  <span style={{ fontSize: 11, color: '#888' }}>WiFi RSSI</span>
                  <span style={{ fontSize: 14, fontWeight: 600, color: rssi > -70 ? '#2D7A3A' : rssi > -85 ? '#f39c12' : '#e74c3c' }}>
                    📶 {rssi} dBm
                  </span>
                </div>
              )}
              {device?.config?.firmware && (
                <div style={S.statusChip}>
                  <span style={{ fontSize: 11, color: '#888' }}>Firmware</span>
                  <span style={{ fontSize: 14, fontWeight: 600, color: '#1a1a1a' }}>{device.config.firmware}</span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Event history */}
        <div style={S.card}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
            <p style={{ ...S.cardLabel, margin: 0 }}>{t('deviceDetail.history')}</p>
            <button onClick={() => { loadDevice(); loadEvents() }}
              style={{ background: 'none', border: 'none', color: '#2D7A3A', fontSize: 13, cursor: 'pointer', padding: 0 }}>
              ↻ {t('deviceDetail.refresh')}
            </button>
          </div>
          {loading ? (
            <p style={{ color: '#888' }}>{t('deviceDetail.loading')}</p>
          ) : events.length === 0 ? (
            <p style={{ color: '#888', textAlign: 'center', padding: '20px 0' }}>{t('deviceDetail.noEvents')}</p>
          ) : (
            events.map((event: any) => (
              <div key={event.id} style={S.eventRow}>
                <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontSize: 18 }}>{EVENT_ICONS[event.type] ?? '📌'}</span>
                  <span style={S.eventLabel}>{event.type}</span>
                </span>
                <span style={S.eventTime}>
                  {new Date(event.createdAt).toLocaleString(lang === 'en' ? 'en-GB' : 'sk-SK', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' })}
                </span>
              </div>
            ))
          )}
        </div>

        {/* Rename */}
        <div style={S.card}>
          <p style={S.cardLabel}>{t('deviceDetail.rename')}</p>
          {renaming ? (
            <div style={{ display: 'flex', gap: 8 }}>
              <input style={{ flex: 1, padding: '10px 14px', borderRadius: 10, border: '1px solid #e0e0e0', fontSize: 15, outline: 'none' }}
                value={newName} onChange={e => setNewName(e.target.value)} placeholder={device?.name} autoFocus />
              <button onClick={handleRename} style={{ padding: '10px 16px', borderRadius: 10, border: 'none', backgroundColor: '#2D7A3A', color: '#fff', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
                {t('deviceDetail.renameSave')}
              </button>
              <button onClick={() => { setRenaming(false); setNewName('') }}
                style={{ padding: '10px 14px', borderRadius: 10, border: 'none', backgroundColor: '#f0f0f0', color: '#666', fontSize: 14, cursor: 'pointer' }}>✕</button>
            </div>
          ) : (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <p style={{ fontSize: 16, fontWeight: 600, color: '#1a1a1a', margin: 0 }}>{device?.name}</p>
              <button onClick={() => { setRenaming(true); setNewName(device?.name || '') }}
                style={{ background: 'none', border: '1px solid #e0e0e0', borderRadius: 8, padding: '6px 14px', fontSize: 13, color: '#555', cursor: 'pointer' }}>
                ✏️ {t('deviceDetail.renameBtn')}
              </button>
            </div>
          )}
        </div>

        {/* Device Key */}
        <div style={{ ...S.card, backgroundColor: '#fafafa' }}>
          <p style={S.cardLabel}>Device Key</p>
          <p style={{ fontSize: 18, fontWeight: 600, color: '#1a1a1a', margin: 0, fontFamily: 'monospace', letterSpacing: 1 }}>
            {device?.deviceKey}
          </p>
          {regenConfirm && <p style={{ fontSize: 13, color: '#2D7A3A', margin: '8px 0 0', fontWeight: 600 }}>✅ {t('deviceDetail.regenConfirm')}</p>}
          <button onClick={handleRegenKey}
            style={{ marginTop: 10, background: 'none', border: '1px solid #e0e0e0', borderRadius: 8, padding: '6px 14px', fontSize: 13, color: '#e74c3c', cursor: 'pointer' }}>
            🔄 {t('deviceDetail.regenKey')}
          </button>
        </div>

      </div>
    </div>
  )
}

const S: Record<string, React.CSSProperties> = {
  container: { minHeight: '100vh', backgroundColor: '#f8f9fa' },
  header: { backgroundColor: '#fff', padding: '24px 20px', borderBottom: '1px solid #f0f0f0', maxWidth: 600, margin: '0 auto' },
  back: { background: 'none', border: 'none', color: '#2D7A3A', fontSize: 17, cursor: 'pointer', padding: 0, marginBottom: 8, display: 'block' },
  title: { fontSize: 24, fontWeight: 600, color: '#1a1a1a', margin: 0 },
  badge: { padding: '4px 12px', borderRadius: 20, fontSize: 13, fontWeight: 500 },
  content: { maxWidth: 600, margin: '0 auto', padding: 20 },
  card: { backgroundColor: '#fff', borderRadius: 16, padding: 20, marginBottom: 12, boxShadow: '0 2px 12px rgba(0,0,0,0.04)' },
  cardLabel: { fontSize: 12, color: '#888', textTransform: 'uppercase' as const, letterSpacing: 0.5, margin: '0 0 12px' },
  statusChip: { display: 'flex', flexDirection: 'column' as const, gap: 2, flex: 1, minWidth: 120 },
  eventRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 0', borderBottom: '1px solid #f5f5f5' },
  eventLabel: { fontSize: 15, color: '#1a1a1a' },
  eventTime: { fontSize: 13, color: '#888', flexShrink: 0, marginLeft: 8 },
}
